import { Search as SearchIcon } from '@mui/icons-material';
import { alpha, Box, Stack, styled, Typography } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import InputBase from '@mui/material/InputBase';
import { useQuery } from '@tanstack/react-query';
import React, { useLayoutEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useDebounce } from 'use-debounce';
import { getActivities } from '../../../api/activities-nim';
import { ActivityGridReadDTO, DocumentType } from '../../../api/dto';
import { useDeviceType } from '../../../hooks/useDeviceType';
import { DocumentColorMapping } from '../../../lib/colors/DocumentColorMapping';
import { formatDateTime } from '../../../lib/date';
import { makeActivityDefaultFilter } from '../../../lib/filters/activity';
import { getRoute, ROUTES } from '../../../routes/routes';
import { StatusChip } from '../../StatusChip';
import loading = toast.loading;

const Search = styled('div')(({ theme }) => ({
    position: 'relative',
    borderRadius: theme.shape.borderRadius,
    backgroundColor: alpha(theme.palette.common.white, 0.15),
    '&:hover': {
        backgroundColor: alpha(theme.palette.common.white, 0.25),
    },
    marginLeft: theme.spacing(4),
    marginRight: theme.spacing(1),
    width: '100%',
    [theme.breakpoints.down('sm')]: {
        marginLeft: theme.spacing(1),
        width: '100%',
    },
}));

const SearchIconWrapper = styled('div')(({ theme }) => ({
    padding: theme.spacing(0, 2),
    height: '100%',
    position: 'absolute',
    pointerEvents: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
    color: 'inherit',
    width: '100%',
    '& .MuiInputBase-input': {
        padding: theme.spacing(1, 1, 1, 0),
        paddingLeft: `calc(1em + ${theme.spacing(4)})`,
        transition: theme.transitions.create('width'),
        width: '100%',
    },
}));

interface SearchBarProps {
    setSearchBarFocused: (value: boolean) => void;
}

export const SearchBar: React.FC<SearchBarProps> = ({ setSearchBarFocused }) => {
    const intl = useIntl();

    const [textFieldValue, setTextFieldValue] = React.useState('');
    const navigate = useNavigate();
    const { isMobile } = useDeviceType();

    const [textFieldDebounced] = useDebounce(textFieldValue, 300, { leading: true, trailing: true });
    const [options, setOptions] = useState<ActivityGridReadDTO[] | null>(null); // This may seem strange, but it is actually needed to achieve the desired effect
    const { data, isFetching, isError, refetch } = useQuery(
        ['global-search', textFieldDebounced],
        ({ signal }) =>
            getActivities({
                filter: makeActivityDefaultFilter(textFieldDebounced),
                config: { signal },
            }).then((result) => result.content),
        {
            enabled: !!textFieldDebounced,
        }
    );

    useLayoutEffect(() => {
        if (textFieldValue.length > 0) {
            if (data !== undefined) {
                setOptions(data);
            }
        } else {
            setOptions(null);
        }
    }, [data, textFieldValue, setOptions]);

    const isDataStale = textFieldValue !== textFieldDebounced || isFetching;

    const inputRef = React.useRef<HTMLInputElement | null>(null);

    return (
        <Search>
            <SearchIconWrapper>
                <SearchIcon />
            </SearchIconWrapper>
            <Autocomplete
                loading={options === null || (isDataStale && options.length === 0)}
                options={options ?? []}
                filterOptions={(o) => o} // Disable client-side filtering
                inputValue={textFieldValue}
                open={!!textFieldValue}
                value={null} // No value is ever stored
                getOptionLabel={(o) => o.id.toString()} // Doesn't really matter
                onInputChange={(_, newValue) => setTextFieldValue(newValue)}
                onBlur={() => setTextFieldValue('')} // Clear on unfocus
                onChange={(e, value) => {
                    if (value !== null) {
                        navigate(
                            getRoute({
                                path: ROUTES.activity.view,
                                params: { id: value.id },
                            })
                        );
                        inputRef.current?.blur();
                    }
                }}
                renderInput={(params) => (
                    <StyledInputBase
                        inputRef={inputRef}
                        ref={params.InputProps.ref}
                        inputProps={params.inputProps}
                        placeholder={intl.formatMessage({ id: 'search.globalSearch.title' })}
                        onFocus={() => setSearchBarFocused(true)}
                        onBlur={() => setSearchBarFocused(false)}
                    />
                )}
                renderOption={(props, option) => (
                    <li {...props} key={option.id}>
                        <Box>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <Typography variant="subtitle2" fontWeight="bold">
                                    <FormattedMessage id="activity.titleExisting" values={{ id: option.id }} />
                                </Typography>
                                <StatusChip
                                    label={option.status.label}
                                    color={DocumentColorMapping[DocumentType.ACTIVITY]![option.status.code]}
                                    size="small"
                                />
                            </Stack>
                            <Typography variant="body1">{option.title}</Typography>
                            <Typography variant="caption" color="text.secondary">
                                <FormattedMessage
                                    id="document.createdBy"
                                    values={{
                                        person: option.createdBy.searchLabel,
                                        date: formatDateTime(new Date(option.createdDate), intl),
                                    }}
                                />
                            </Typography>
                        </Box>
                    </li>
                )}
                ListboxProps={{
                    sx: {
                        opacity: isDataStale ? 0.5 : 1,
                    },
                }}
                sx={{
                    width: '100%',
                }}
            />
        </Search>
    );
};
